import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import {
  useDevicesWithFacilitiesQuery,
  useFacilityFloorOptionsQuery,
  useFacilityTypesQuery,
  useManualIncidentCategoriesQuery,
} from '@/graphql/generated/hooks'
import {
  DeviceOrderField,
  DeviceType,
  OrderDirection,
} from '@/graphql/generated/schemas'
import { CustomFilterIProps } from '@/utils/forms/optionBuilders'
import { MIN_CHAR_COUNT } from '@/utils/forms/validation'

interface UseIncidentModalBodyIProps {
  facilityId?: string
  floorId?: string
  facilityName?: string
}

export const useIncidentCreationData = ({
  facilityId,
  facilityName,
  floorId,
}: UseIncidentModalBodyIProps) => {
  const hasFacilityId = Boolean(facilityId)
  const hasFloorId = Boolean(floorId)
  const isSearchFacilityByAddressEnabled = useFeatureFlag(
    FeatureFlag.searchFacilityByAddress
  )

  const facilityFilterInput = isSearchFacilityByAddressEnabled
    ? {
        OR: [{ nameLike: facilityName }, { addressLike: facilityName }],
      }
    : { nameLike: facilityName }

  const { data: facilitiesData, loading: isFacilitiesLoading } =
    useFacilityTypesQuery({
      fetchPolicy: 'network-only',
      skip: hasFacilityId || hasFloorId,
      variables: {
        filter:
          facilityName?.length >= MIN_CHAR_COUNT
            ? facilityFilterInput
            : undefined,
      },
    })
  const { data: floorsData, loading: isFloorsLoading } =
    useFacilityFloorOptionsQuery({
      variables: {
        id: facilityId,
      },
      fetchPolicy: 'network-only',
      skip: !hasFacilityId,
    })
  const { data: devicesData } = useDevicesWithFacilitiesQuery({
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        facilityIdIn: [facilityId],
        floorIdIn: [floorId],
        typeNot: DeviceType.Checkpoint,
      },
      orderBy: [
        {
          direction: OrderDirection.Asc,
          field: DeviceOrderField.Name,
        },
      ],
    },
    skip: !hasFacilityId || !hasFloorId,
  })
  const { data: categoriesData } = useManualIncidentCategoriesQuery({
    nextFetchPolicy: 'network-only',
    errorPolicy: 'all',
  })

  const customFacilitiesFilter = (
    facilityOption: CustomFilterIProps,
    input: string
  ) => {
    const { label, address } = facilityOption.data
    const lowerCaseInput = input.toLowerCase()

    // Short circuit if label matches
    if (label.toLowerCase().includes(lowerCaseInput)) {
      return true
    }

    if (!isSearchFacilityByAddressEnabled) {
      return false
    }

    return address?.join('').toLowerCase().includes(lowerCaseInput) ?? false
  }

  return {
    isFacilitiesLoading,
    isFloorsLoading,
    facilitiesData,
    floorsData,
    devicesData,
    categoriesData,
    customFacilitiesFilter,
    isSearchFacilityByAddressEnabled,
  }
}
